function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => {
    if (video.classList.contains('meet-luncher-carousel-video')) return;
    video.pause();
  });
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (poster) poster.addEventListener('click', this.loadContent.bind(this));
  }

  connectedCallback() {
    if (!this.hasAttribute('data-autoplay')) return;

    // Active slide: start immediately (slider / first media)
    if (this.closest('.product__media-item.is-active')) {
      this.loadContent(false);
    }

    // Columns/stacked galleries show videos that are not is-active — autoplay when in view
    if (this.classList.contains('product-gallery-video-autoplay')) {
      this.observeGalleryAutoplay();
    }
  }

  disconnectedCallback() {
    if (this._galleryAutoplayIO) {
      this._galleryAutoplayIO.disconnect();
      this._galleryAutoplayIO = null;
    }
  }

  observeGalleryAutoplay() {
    if (this._galleryAutoplayIO || !('IntersectionObserver' in window)) {
      if (!('IntersectionObserver' in window) && !this.getAttribute('loaded')) {
        this.loadContent(false, { pauseOthers: false });
      }
      return;
    }

    const target = this.closest('.product__media-item') || this;
    this._galleryAutoplayIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadContent(false, { pauseOthers: false });
          } else {
            const video = this.querySelector('video');
            if (video && !video.paused) {
              try {
                video.pause();
              } catch (e) {
                /* ignore */
              }
            }
          }
        });
      },
      { rootMargin: '80px 0px', threshold: 0.2 }
    );
    this._galleryAutoplayIO.observe(target);
  }

  playDeferredVideo(video) {
    if (!video) return;
    video.muted = true;
    video.removeAttribute('controls');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    if (video.getAttribute('preload') === 'metadata') {
      video.setAttribute('preload', 'auto');
    }

    const tryPlay = () => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          video.dataset.mpGalleryPlayPending = '1';
        });
      }
    };

    tryPlay();
    if (video.readyState < 2) {
      video.addEventListener('canplay', tryPlay, { once: true });
      video.addEventListener('loadeddata', tryPlay, { once: true });
    }
  }

  loadContent(focus = true, options = {}) {
    const pauseOthers = options.pauseOthers !== false;
    if (pauseOthers) window.pauseAllMedia();

    if (!this.getAttribute('loaded')) {
      const template = this.querySelector('template');
      if (!template || !template.content || !template.content.firstElementChild) return;

      const content = document.createElement('div');
      content.appendChild(template.content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (!deferredElement) return;
      if (focus && deferredElement.focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO') {
        if (deferredElement.getAttribute('autoplay') || this.hasAttribute('data-autoplay')) {
          this.playDeferredVideo(deferredElement);
        }
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    } else {
      const video = this.querySelector('video');
      if (video) this.playDeferredVideo(video);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

(function bindGalleryVideoPlayRetry() {
  const retry = () => {
    document.querySelectorAll('deferred-media.product-gallery-video-autoplay video[data-mp-gallery-play-pending="1"]').forEach((video) => {
      delete video.dataset.mpGalleryPlayPending;
      const host = video.closest('deferred-media');
      if (host && typeof host.playDeferredVideo === 'function') host.playDeferredVideo(video);
      else {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      }
    });
  };
  document.addEventListener('touchstart', retry, { passive: true });
  document.addEventListener('click', retry);
})();

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = this.dataset.loop === 'true';
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    this.bindSwipeGestures();
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.slider.scrollLeft + this.slider.clientWidth >= this.slider.scrollWidth - 1) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    if (this.enableSliderLooping) {
      const isNext = event.currentTarget.name === 'next';
      const atStart = this.currentPage <= 1 || this.slider.scrollLeft <= 0;
      const atEnd = this.currentPage >= this.totalPages || this.slider.scrollLeft + this.slider.clientWidth >= this.slider.scrollWidth - 1;

      if (isNext && atEnd) {
        this.setSlidePosition(0);
        return;
      }

      if (!isNext && atStart) {
        this.setSlidePosition((this.totalPages - 1) * this.sliderItemOffset);
        return;
      }
    }

    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
      behavior: 'smooth',
    });
  }

  bindSwipeGestures() {
    if (!this.slider) return;

    let startX = 0;
    let currentX = 0;
    let isPointerDown = false;
    const swipeThreshold = 40;

    this.slider.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.buttons !== 1) return;
        startX = event.clientX;
        currentX = event.clientX;
        isPointerDown = true;
      },
      { passive: true }
    );

    this.slider.addEventListener(
      'pointermove',
      (event) => {
        if (!isPointerDown) return;
        currentX = event.clientX;
      },
      { passive: true }
    );

    const handlePointerEnd = () => {
      if (!isPointerDown) return;
      const deltaX = currentX - startX;
      isPointerDown = false;

      if (Math.abs(deltaX) < swipeThreshold) return;

      const direction = deltaX < 0 ? 'next' : 'previous';
      const button = direction === 'next' ? this.nextButton : this.prevButton;
      if (button) button.click();
    };

    this.slider.addEventListener('pointerup', handlePointerEnd, { passive: true });
    this.slider.addEventListener('pointercancel', handlePointerEnd, { passive: true });
    this.slider.addEventListener('pointerleave', handlePointerEnd, { passive: true });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
    this.setupVariantNameHover();
  }

  setupVariantNameHover() {
    this.querySelectorAll('.faux-swatch-item, .product-form__variant-image-item').forEach((item) => {
      const input = item.querySelector('input[type="radio"]');
      if (!input) return;

      item.addEventListener('mouseenter', () => {
        const fieldset = item.closest('.product-form__input');
        const labelEl = fieldset?.querySelector('[data-selected-value]');
        if (labelEl) labelEl.textContent = input.value;
      });
      item.addEventListener('mouseleave', () => {
        const fieldset = item.closest('.product-form__input');
        const checked = fieldset?.querySelector('input[type="radio"]:checked');
        const labelEl = fieldset?.querySelector('[data-selected-value]');
        if (labelEl && checked) labelEl.textContent = checked.value;
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

function updateQuickViewAddToCartState(quickViewModal, productAvailable, isPreorder) {
  if (!quickViewModal) return;
  const submitButton = quickViewModal.querySelector('[data-quick-view-atc]');
  if (!submitButton) return;
  const preorderPropertyInput = quickViewModal.querySelector('[data-quick-view-preorder-property]');
  const preorderNote = quickViewModal.querySelector('[data-quick-view-preorder-note]');
  let labelEl = quickViewModal.querySelector('[data-quick-view-atc-label]');
  if (!labelEl) {
    labelEl = submitButton.querySelector('span:not(.loading__spinner)');
  }
  const addText = (window.variantStrings && window.variantStrings.addToCart) || 'ADD TO CART';
  const soldText = (window.variantStrings && window.variantStrings.soldOut) || 'SOLD OUT';
  const preorderText = 'PREORDER';
  if (productAvailable) {
    submitButton.disabled = false;
    submitButton.removeAttribute('aria-disabled');
    submitButton.classList.remove('quick-view-lite__atc--soldout');
    submitButton.classList.toggle('is-preorder', !!isPreorder);
    if (labelEl) labelEl.textContent = isPreorder ? preorderText : addText;
    if (preorderPropertyInput) preorderPropertyInput.disabled = !isPreorder;
    if (preorderNote) preorderNote.hidden = !isPreorder;
  } else {
    submitButton.disabled = true;
    submitButton.setAttribute('aria-disabled', 'true');
    submitButton.classList.add('quick-view-lite__atc--soldout');
    submitButton.classList.remove('is-preorder');
    if (preorderPropertyInput) preorderPropertyInput.disabled = true;
    if (preorderNote) preorderNote.hidden = true;
    if (labelEl) labelEl.textContent = soldText;
  }
}
window.updateQuickViewAddToCartState = updateQuickViewAddToCartState;

/** Collection product cards: sync quick-add button with swatch variant availability. */
function updateCollectionCardQuickAddFromData(cardItem, productDataEl, explicitVariantId) {
  if (!cardItem || !productDataEl) return;

  const quickAddBtn =
    cardItem.querySelector('.js-product-quick-add') || cardItem.querySelector('.product-grid-quick-add-below');
  if (!quickAddBtn) return;

  const isGiftCard = String(productDataEl.dataset.isGiftCard || 'false') === 'true';
    if (isGiftCard) {
    if (quickAddBtn.dataset.hadQuickAdd === undefined) {
      quickAddBtn.dataset.hadQuickAdd = quickAddBtn.classList.contains('js-product-quick-add') ? '1' : '0';
    }
    quickAddBtn.classList.remove('js-product-quick-add');
    quickAddBtn.classList.remove('is-sold-out');
    quickAddBtn.removeAttribute('data-id');
    const avenirEl = quickAddBtn.querySelector('.avenir');
    if (avenirEl) {
      avenirEl.innerHTML = '<span class="mobile-hide">SHOP </span>NOW';
    } else {
      quickAddBtn.textContent = 'SHOP NOW';
    }
    return;
  }


  const available = String(productDataEl.dataset.available ?? 'true') === 'true';
  const notifyMeText =
    (window.variantStrings && window.variantStrings.notifyMe) || 'NOTIFY ME';
  const variantId = explicitVariantId || productDataEl.dataset.variantId;
  const avenirEl = quickAddBtn.querySelector('.avenir');

  if (!quickAddBtn.dataset.defaultQuickAddHtml && avenirEl) {
    quickAddBtn.dataset.defaultQuickAddHtml = avenirEl.innerHTML;
  }
  if (quickAddBtn.dataset.hadQuickAdd === undefined) {
    quickAddBtn.dataset.hadQuickAdd = quickAddBtn.classList.contains('js-product-quick-add') ? '1' : '0';
  }

  if (!available) {
    if (quickAddBtn.dataset.hadQuickAdd === '1') {
      quickAddBtn.classList.remove('js-product-quick-add');
    }
    quickAddBtn.classList.add('is-sold-out');
    quickAddBtn.removeAttribute('data-id');
    if (avenirEl) avenirEl.innerHTML = notifyMeText;
    else quickAddBtn.textContent = notifyMeText;
    return;
  }

  if (quickAddBtn.dataset.defaultQuickAddHtml && avenirEl) {
    avenirEl.innerHTML = quickAddBtn.dataset.defaultQuickAddHtml;
  }
  if (quickAddBtn.dataset.hadQuickAdd === '1') {
    quickAddBtn.classList.add('js-product-quick-add');
  }
  quickAddBtn.classList.remove('is-sold-out');
  if (variantId) quickAddBtn.dataset.id = variantId;
 if (typeof window.syncCollectionQuickAddMonogramState === 'function') {
    window.syncCollectionQuickAddMonogramState(quickAddBtn, productDataEl);
  }
  const priceWrap = quickAddBtn.querySelector('.product-grid-price');
  if (!priceWrap) return;

  const finalPrice = productDataEl.dataset.finalPrice || '';
  if (!finalPrice) return;

  const isSale = String(productDataEl.dataset.isSale || 'false') === 'true';
  const comparePrice = productDataEl.dataset.comparePrice || '';

  if (isSale && comparePrice) {
    priceWrap.innerHTML = ' - <s>' + comparePrice + '</s>&nbsp;<span class="flash-price">' + finalPrice + '</span>';
  } else {
    priceWrap.innerHTML = ' - <span class="flash-price">' + finalPrice + '</span>';
  }
}
window.updateCollectionCardQuickAddFromData = updateCollectionCardQuickAddFromData;
/** SHOP NOW / NOTIFY ME collection buttons: redirect to product page (incl. after swatch swap). */
document.addEventListener('click', function (e) {
  const button = e.target.closest('.product-grid-quick-add-below:not(.js-product-quick-add)');
  if (!button || button.matches('a[href]')) return;
  e.preventDefault();
  e.stopPropagation();
  const cardItem = button.closest('[data-product-card-item]');
  if (!cardItem) return;
  const productLink = cardItem.querySelector('.card__heading a, a[href*="/products/"]');
  if (productLink) window.location.href = productLink.href;
});

function initCollectionGiftCardQuickAddGuard() {
  document.querySelectorAll('[data-product-data] [data-is-gift-card="true"]').forEach((productDataEl) => {
    const cardItem = productDataEl.closest('[data-product-card-item]');
    if (cardItem) {
      updateCollectionCardQuickAddFromData(cardItem, productDataEl);
    }
  });
}
window.initCollectionGiftCardQuickAddGuard = initCollectionGiftCardQuickAddGuard;
document.addEventListener('DOMContentLoaded', initCollectionGiftCardQuickAddGuard);

/** Featured collection / product section: desktop infinite slider, swatches, quick view ATC (loaded once via theme). */
function cleanupFcopSliderClones(slider, slidePrefix) {
  if (!slider) return;
  slider.querySelectorAll('li[id^="' + slidePrefix + '"]').forEach((li) => {
    if (li.id.includes('-clone')) li.remove();
  });
}

function initFeaturedCollectionOrProductSection(sectionEl) {
  if (!sectionEl) return;
  const sectionId = sectionEl.dataset.id;
  if (!sectionId) return;

  if (sectionEl._fcopAbort) {
    sectionEl._fcopAbort.abort();
  }
  sectionEl._fcopAbort = new AbortController();
  const fcopSignal = sectionEl._fcopAbort.signal;

  const quickAddDesktop = sectionEl.dataset.quickAddText || 'QUICK ADD';
  const quickAddMobile = sectionEl.dataset.quickAddTextMobile || quickAddDesktop;

  const wrapper = sectionEl.querySelector('[data-fcop-slider-wrapper]');
  const slider = wrapper ? wrapper.querySelector('ul[id^="Slider-"]') : null;
  const prevBtn = sectionEl.querySelector('[data-slider-prev]');
  const nextBtn = sectionEl.querySelector('[data-slider-next]');

  const slidePrefix = 'Slide-' + sectionId;
  const listSlideItems = () =>
    slider ? Array.from(slider.querySelectorAll('li')).filter((li) => li.id && li.id.startsWith(slidePrefix)) : [];

  let sliderItems = listSlideItems();

  if (slider && prevBtn && nextBtn && sliderItems.length > 0) {
    let isAnimating = false;
    let isJumping = false;
    let scrollTimeout;
    let sliderReadyAttempts = 0;

    cleanupFcopSliderClones(slider, slidePrefix);
    sliderItems = listSlideItems();

    function setupInfiniteLoop() {
      if (sliderItems.length === 0) return;
      const realSlides = sliderItems.filter((item) => !item.id.includes('-clone'));
      if (realSlides.length === 0) return;
      const firstSlide = realSlides[0];
      const slideWidth = firstSlide.offsetWidth;
      if (slideWidth < 1) return false;
      const gap = realSlides.length > 1 ? realSlides[1].offsetLeft - realSlides[0].offsetLeft - slideWidth : 0;
      const denom = slideWidth + (gap > 0 ? gap : 0);
      const slidesPerView = denom > 0 ? Math.ceil(slider.clientWidth / denom) || 1 : 1;
      const clonesToAdd = Math.min(slidesPerView + 1, realSlides.length);
      for (let i = 0; i < clonesToAdd; i++) {
        const clone = realSlides[i].cloneNode(true);
        clone.id = realSlides[i].id + '-clone-end';
        slider.appendChild(clone);
      }
      for (let i = realSlides.length - clonesToAdd; i < realSlides.length; i++) {
        const clone = realSlides[i].cloneNode(true);
        clone.id = realSlides[i].id + '-clone-start';
        slider.insertBefore(clone, realSlides[0]);
      }
      sliderItems = listSlideItems();
      const firstRealSlide = sliderItems.find((item) => !item.id.includes('-clone'));
      if (firstRealSlide) {
        function scrollToFirst() {
          slider.scrollLeft = firstRealSlide.offsetLeft;
        }
        requestAnimationFrame(() => {
          scrollToFirst();
          setTimeout(scrollToFirst, 150);
        });
        window.addEventListener('load', scrollToFirst, { once: true, signal: fcopSignal });
      }
      return true;
    }

    function runSliderSetupWhenReady() {
      if (setupInfiniteLoop()) return;
      if (sliderReadyAttempts++ > 60) return;
      requestAnimationFrame(runSliderSetupWhenReady);
    }

    function getSlideScrollAmount() {
      if (sliderItems.length === 0) return 0;
      const firstSlide = sliderItems[0];
      if (sliderItems.length > 1) return sliderItems[1].offsetLeft - firstSlide.offsetLeft;
      return firstSlide.offsetWidth;
    }

    function checkInfiniteLoop() {
      if (isJumping) return;
      const scrollLeft = slider.scrollLeft;
      const realSlides = sliderItems.filter((item) => !item.id.includes('-clone'));
      if (realSlides.length === 0) return;
      const firstRealSlide = realSlides[0];
      const lastRealSlide = realSlides[realSlides.length - 1];
      const endClones = sliderItems.filter((item) => item.id.includes('-clone-end'));
      const startClones = sliderItems.filter((item) => item.id.includes('-clone-start'));
      const firstEndClone = endClones.length > 0 ? endClones[0] : null;
      const lastStartClone = startClones.length > 0 ? startClones[startClones.length - 1] : null;
      if (!firstRealSlide || !lastRealSlide) return;
      const tolerance = 10;
      if (firstEndClone && scrollLeft >= firstEndClone.offsetLeft - tolerance) {
        isJumping = true;
        slider.style.scrollBehavior = 'auto';
        slider.scrollLeft = firstRealSlide.offsetLeft;
        requestAnimationFrame(() => {
          slider.style.scrollBehavior = 'smooth';
          setTimeout(() => {
            isJumping = false;
          }, 50);
        });
      } else if (lastStartClone && scrollLeft <= lastStartClone.offsetLeft + tolerance) {
        isJumping = true;
        slider.style.scrollBehavior = 'auto';
        slider.scrollLeft = lastRealSlide.offsetLeft;
        requestAnimationFrame(() => {
          slider.style.scrollBehavior = 'smooth';
          setTimeout(() => {
            isJumping = false;
          }, 50);
        });
      }
    }

    prevBtn.addEventListener(
      'click',
      function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (sliderItems.length === 0 || isAnimating) return;
        isAnimating = true;
        slider.scrollBy({ left: -getSlideScrollAmount(), behavior: 'smooth' });
        setTimeout(() => {
          isAnimating = false;
          checkInfiniteLoop();
        }, 550);
      },
      { signal: fcopSignal }
    );

    nextBtn.addEventListener(
      'click',
      function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (sliderItems.length === 0 || isAnimating) return;
        isAnimating = true;
        slider.scrollBy({ left: getSlideScrollAmount(), behavior: 'smooth' });
        setTimeout(() => {
          isAnimating = false;
          checkInfiniteLoop();
        }, 550);
      },
      { signal: fcopSignal }
    );

    slider.addEventListener(
      'scroll',
      function () {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(checkInfiniteLoop, 100);
      },
      { passive: true, signal: fcopSignal }
    );

    runSliderSetupWhenReady();

    let isDown = false;
    let startX;
    let scrollLeftStart;

    slider.addEventListener('mousedown', (e) => {
      if (e.target.closest('button, a, .product-swatch, .quick-add')) return;
      isDown = true;
      slider.style.cursor = 'grabbing';
      startX = (e.pageX || e.clientX) - slider.offsetLeft;
      scrollLeftStart = slider.scrollLeft;
      e.preventDefault();
    }, { signal: fcopSignal });

    const stopDrag = () => {
      if (isDown) {
        isDown = false;
        slider.style.cursor = 'grab';
        setTimeout(checkInfiniteLoop, 100);
      }
    };

    document.addEventListener('mouseup', stopDrag, { signal: fcopSignal });

    slider.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      const x = (e.pageX || e.clientX) - slider.offsetLeft;
      slider.scrollLeft = scrollLeftStart - (x - startX) * 2;
    }, { signal: fcopSignal });

    slider.addEventListener(
      'wheel',
      (e) => {
        if (!e.target.closest('button, a, .product-swatch, .quick-add')) {
          slider.scrollLeft += e.deltaY;
          e.preventDefault();
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(checkInfiniteLoop, 150);
        }
      },
      { passive: false, signal: fcopSignal }
    );

    slider.style.cursor = 'grab';
  }

  sectionEl.querySelectorAll('[data-collection-card]').forEach((card) => {
    if (card.dataset.fcopCardInit === '1') return;

    const slideLi = card.closest('li[id^="Slide-"]');
    if (slideLi && slideLi.id.includes('-clone')) return;

    card.dataset.fcopCardInit = '1';

    const slides = card.querySelectorAll('.product-slide');
    const titleDisplay = card.querySelector('[data-product-title-display] a');
    const priceDisplay = card.querySelector('[data-price]');
    const comparePriceDisplay = card.querySelector('[data-compare-price]');
    const quickAddBtn = card.querySelector('[data-quick-add-btn]');
    const quickAddText = card.querySelector('[data-quick-add-text]');
    const modalOpener = quickAddBtn ? quickAddBtn.closest('modal-opener') : null;
    const modalSelector = modalOpener ? modalOpener.getAttribute('data-modal') : null;
    const quickViewModal = modalSelector ? document.querySelector(modalSelector) : null;

    let defaultProductIndex = 0;
    const activeSlide = card.querySelector('.product-slide.active');
    if (activeSlide && activeSlide.dataset.productIndex !== undefined) {
      defaultProductIndex = parseInt(activeSlide.dataset.productIndex, 10);
    }

    function getSwatchSlideIndex(swatch) {
      if (swatch.matches('[data-more-products-trigger]') || swatch.matches('[data-more-products-close]')) return null;
      const rawIdx = swatch.dataset.swatchIndex;
      if (rawIdx !== undefined && rawIdx !== '') {
        const n = parseInt(rawIdx, 10);
        if (!Number.isNaN(n)) return n;
      }
      const productId = swatch.dataset.productId;
      if (productId && slides.length) {
        for (let i = 0; i < slides.length; i++) {
          if (slides[i].dataset.productId === productId) return i;
        }
      }
      return null;
    }

    function updateProductDisplay(index) {
      card.querySelectorAll('.product-swatch').forEach((s) => {
        if (s.matches('[data-more-products-trigger]') || s.matches('[data-more-products-close]')) return;
        const swatchIdx = getSwatchSlideIndex(s);
        s.classList.toggle('active', swatchIdx !== null && swatchIdx === index);
      });

      slides.forEach((slide) => {
        slide.classList.remove('active');
        if (parseInt(slide.dataset.productIndex, 10) !== index) return;
        slide.classList.add('active');

        const productUrl = slide.dataset.productUrl;
        const productTitle = slide.dataset.productTitle;
        const productPrice = slide.dataset.productPrice;
        const productComparePrice = slide.dataset.productComparePrice;
        const productAvailable = slide.dataset.productAvailable === 'true';
        const productPreorder = slide.dataset.productPreorder === 'true';

        if (priceDisplay) {
          priceDisplay.textContent = productPrice;
          priceDisplay.classList.toggle('sale-price', Boolean(productComparePrice));
        }

        if (comparePriceDisplay) {
          if (productComparePrice) {
            comparePriceDisplay.textContent = productComparePrice;
            comparePriceDisplay.style.display = '';
          } else {
            comparePriceDisplay.textContent = '';
            comparePriceDisplay.style.display = 'none';
          }
        }

        if (quickAddBtn) quickAddBtn.dataset.productUrl = productUrl;

        if (quickViewModal) {
          const quickViewImage = quickViewModal.querySelector('[data-quick-view-image]');
          const quickViewTitle = quickViewModal.querySelector('[data-quick-view-title]');
          const quickViewPrice = quickViewModal.querySelector('[data-quick-view-price]');
          const quickViewLink = quickViewModal.querySelector('[data-quick-view-link]');
          const quickViewDescription = quickViewModal.querySelector('[data-quick-view-description]');
          const quickViewColor = quickViewModal.querySelector('[data-quick-view-color]');
          const quickViewVariant = quickViewModal.querySelector('[data-quick-view-variant]');
          const quickViewSwatches = quickViewModal.querySelectorAll('[data-quick-view-swatch]');
          if (quickViewImage && slide.dataset.productImage) quickViewImage.src = slide.dataset.productImage;
          if (quickViewImage) quickViewImage.alt = productTitle;
          if (quickViewTitle) {
            const displayTitle =
              (quickAddBtn && quickAddBtn.dataset.productDisplayTitle) ||
              (titleDisplay && titleDisplay.textContent ? titleDisplay.textContent.trim() : '') ||
              productTitle;
            quickViewTitle.textContent = displayTitle;
          }
          if (quickViewPrice) quickViewPrice.textContent = productPrice;
          if (quickViewLink) quickViewLink.href = productUrl;
          if (quickViewDescription) quickViewDescription.textContent = slide.dataset.productDescription || '';
          if (quickViewColor) quickViewColor.textContent = slide.dataset.productColor || '';
          if (quickViewVariant) quickViewVariant.value = slide.dataset.variantId || '';
          quickViewSwatches.forEach((sw) => {
            sw.classList.toggle('is-active', parseInt(sw.dataset.swatchIndex, 10) === index);
          });
          updateQuickViewAddToCartState(quickViewModal, productAvailable, productPreorder);
           if (typeof window.updateQuickViewMonogramState === 'function') {
            window.updateQuickViewMonogramState(
              quickViewModal,
              slide.dataset.productIsMonogram === 'true',
              slide.dataset.productForceMonogram === 'true'
            );
          }
        }

        const soldOutText = (window.variantStrings && window.variantStrings.soldOut) || 'SOLD OUT';
        const quickAddTextMobile = card.querySelector('[data-quick-add-text-mobile]');
        const quickAddCompare = card.querySelector('[data-compare-price]');
        const quickAddPrice = card.querySelector('[data-price]');
        const quickAddDash =
          quickAddBtn &&
          Array.from(quickAddBtn.children).find(
            (el) => el.tagName === 'SPAN' && !el.hasAttribute('data-quick-add-text') && !el.hasAttribute('data-quick-add-text-mobile') && el.textContent.trim() === '-'
          );

        if (!productAvailable) {
          if (quickAddBtn) {
            quickAddBtn.disabled = true;
            quickAddBtn.setAttribute('aria-disabled', 'true');
            quickAddBtn.classList.add('quick-add--soldout');
            quickAddBtn.classList.remove('is-preorder');
          }
          if (quickAddText) quickAddText.textContent = soldOutText;
          if (quickAddTextMobile) quickAddTextMobile.textContent = soldOutText;
          if (quickAddDash) quickAddDash.style.display = 'none';
          if (quickAddCompare) quickAddCompare.style.display = 'none';
          if (quickAddPrice) quickAddPrice.style.display = 'none';
        } else {
          if (quickAddBtn) {
            quickAddBtn.disabled = false;
            quickAddBtn.removeAttribute('aria-disabled');
            quickAddBtn.classList.remove('quick-add--soldout');
            quickAddBtn.classList.toggle('is-preorder', productPreorder);
          }
          if (quickAddText) quickAddText.textContent = quickAddDesktop;
          if (quickAddTextMobile) quickAddTextMobile.textContent = quickAddMobile;
          if (quickAddDash) quickAddDash.style.display = '';
          if (quickAddCompare) quickAddCompare.style.display = productComparePrice ? '' : 'none';
          if (quickAddPrice) quickAddPrice.style.display = '';
        }

        const soldoutImageAboveAdd = card.querySelector('[data-soldout-image-above-add]');
        if (soldoutImageAboveAdd) {
          soldoutImageAboveAdd.classList.toggle('is-visible', !productAvailable);
        }
      });
    }

    function loadSwatchImages(container) {
      if (!container) return;
      container.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.loading = 'eager';
      });
    }

    card.querySelectorAll('.product-swatch').forEach((swatch) => {
      swatch.addEventListener(
        'mouseenter',
        function () {
          const idx = getSwatchSlideIndex(swatch);
          if (idx === null || Number.isNaN(idx)) return;
          updateProductDisplay(idx);
        },
        { signal: fcopSignal }
      );
      swatch.addEventListener(
        'click',
        function (e) {
          e.preventDefault();
          const idx = getSwatchSlideIndex(swatch);
          if (idx === null || Number.isNaN(idx)) return;
          defaultProductIndex = idx;
          updateProductDisplay(idx);
        },
        { signal: fcopSignal }
      );
    });

    card.addEventListener(
      'mouseleave',
      function () {
        updateProductDisplay(defaultProductIndex);
      },
      { signal: fcopSignal }
    );

    const moreProductsTrigger = card.querySelector('[data-more-products-trigger]');
    const moreProductsClose = card.querySelector('[data-more-products-close]');
    const swatchesExpanded = card.querySelector('[data-swatches-expanded]');

    if (moreProductsTrigger && swatchesExpanded) {
      moreProductsTrigger.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          swatchesExpanded.style.display = 'contents';
          moreProductsTrigger.style.display = 'none';
          loadSwatchImages(swatchesExpanded);
        },
        { signal: fcopSignal }
      );

      if (moreProductsClose) {
        moreProductsClose.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            swatchesExpanded.style.display = 'none';
            moreProductsTrigger.style.display = '';
          },
          { signal: fcopSignal }
        );
      }
    }

    loadSwatchImages(card.querySelector('[data-swatches-container]'));

    if (quickViewModal) {
      const quickViewForm = quickViewModal.querySelector('[data-quick-view-form]');
      if (quickViewForm) {
        const submitButton = quickViewForm.querySelector('[data-quick-view-atc]');
        const addToCartFromQuickView = function (event) {
          if (event) event.preventDefault();
          const loadingSpinner = quickViewForm.querySelector('.loading__spinner');
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

          if (submitButton && (submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true')) return;
          if (submitButton) {
            submitButton.setAttribute('aria-disabled', 'true');
            submitButton.classList.add('loading');
          }
          if (loadingSpinner) loadingSpinner.classList.remove('hidden');

          const config =
            typeof fetchConfig === 'function'
              ? fetchConfig('javascript')
              : { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } };

          config.headers = config.headers || {};
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          if (config.headers['Content-Type']) delete config.headers['Content-Type'];

          const formData = new FormData(quickViewForm);
          if (typeof window.appendMonogramRoutingProperties === 'function') {
            window.appendMonogramRoutingProperties(formData);
          }
          if (typeof window.applyQuickViewMonogramProperties === 'function') {
            window.applyQuickViewMonogramProperties(formData, quickViewModal);
          }
          if (cart && typeof cart.getSectionsToRender === 'function') {
            formData.append(
              'sections',
              cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;

          const cartAddUrl =
            (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';

          fetch(cartAddUrl, config)
            .then((response) => response.text())
            .then((text) => {
              try {
                return JSON.parse(text);
              } catch (parseError) {
                throw new Error('Quick view add-to-cart returned non-JSON response');
              }
            })
            .then((response) => {
              if (response.status) return;
              if (typeof quickViewModal.hide === 'function') quickViewModal.hide();

              if (cart && typeof cart.renderContents === 'function') {
                const sectionIds = cart.getSectionsToRender().map((section) => section.id);
                const sectionsUrl = `${window.location.pathname}?sections=${sectionIds.join(',')}`;
                fetch(sectionsUrl)
                  .then((sectionRes) => sectionRes.json())
                  .then((sectionsPayload) => {
                    cart.renderContents({
                      id: response.id,
                      sections: sectionsPayload,
                    });
                  })
                  .catch(() => {
                    if (typeof cart.open === 'function') cart.open();
                  });
              }
            })
            .catch((error) => {
              console.error(error);
            })
            .finally(() => {
              if (submitButton) {
                submitButton.classList.remove('loading');
                submitButton.removeAttribute('aria-disabled');
              }
              if (loadingSpinner) loadingSpinner.classList.add('hidden');
            });
        };

        quickViewForm.addEventListener('submit', addToCartFromQuickView, { signal: fcopSignal });
        if (submitButton) submitButton.addEventListener('click', addToCartFromQuickView, { signal: fcopSignal });
      }

      quickViewModal.querySelectorAll('[data-quick-view-swatch]').forEach((swatch) => {
        swatch.addEventListener(
          'click',
          function (e) {
            e.preventDefault();
            const idx = getSwatchSlideIndex(swatch);
            if (idx === null || Number.isNaN(idx)) return;
            defaultProductIndex = idx;
            updateProductDisplay(idx);
          },
          { signal: fcopSignal }
        );
      });
    }

    updateProductDisplay(defaultProductIndex);
  });
}

function initAllFeaturedCollectionOrProductSections() {
  document.querySelectorAll('[data-featured-collection-or-product]').forEach(initFeaturedCollectionOrProductSection);
}
window.initAllFeaturedCollectionOrProductSections = initAllFeaturedCollectionOrProductSections;
window.initFeaturedCollectionOrProductSection = initFeaturedCollectionOrProductSection;

window.resetFeaturedCollectionOrProductSection = function resetFeaturedCollectionOrProductSection(sectionEl) {
  if (!sectionEl) return;
  const sectionId = sectionEl.dataset.id;
  if (sectionId) {
    const wrapper = sectionEl.querySelector('[data-fcop-slider-wrapper]');
    const slider = wrapper ? wrapper.querySelector('ul[id^="Slider-"]') : null;
    cleanupFcopSliderClones(slider, 'Slide-' + sectionId);
  }
  if (sectionEl._fcopAbort) {
    sectionEl._fcopAbort.abort();
    sectionEl._fcopAbort = null;
  }
  sectionEl.querySelectorAll('[data-collection-card]').forEach((card) => {
    delete card.dataset.fcopCardInit;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllFeaturedCollectionOrProductSections);
} else {
  initAllFeaturedCollectionOrProductSections();
}

window.addEventListener('load', () => {
  document.querySelectorAll('[data-featured-collection-or-product]').forEach((sectionEl) => {
    resetFeaturedCollectionOrProductSection(sectionEl);
  });
  initAllFeaturedCollectionOrProductSections();
});

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.querySelector && event.target.querySelector('[data-featured-collection-or-product]');
  if (root) {
    resetFeaturedCollectionOrProductSection(root);
    initFeaturedCollectionOrProductSection(root);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector && event.target.querySelector('[data-featured-collection-or-product]');
  if (root) resetFeaturedCollectionOrProductSection(root);
});
function initCollectionCardImageLinks(root) {
  const scope = root || document;
  scope
    .querySelectorAll('.card__media.card__media--has-image-link:not(.card__media--collection-slider)')
    .forEach((mediaEl) => {
      if (mediaEl.dataset.imageLinkBound === 'true') return;
      mediaEl.dataset.imageLinkBound = 'true';

      mediaEl.addEventListener('click', (event) => {
        const interactiveTarget = event.target.closest('a, button, input, select, textarea, label');
        if (interactiveTarget) return;

        const link = mediaEl.querySelector('.card__media-click-overlay[href]');
        if (!link || !link.href) return;

        window.location.href = link.href;
      });
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCollectionCardImageLinks());
} else {
  initCollectionCardImageLinks();
}

document.addEventListener('shopify:section:load', (event) => {
  initCollectionCardImageLinks(event.target || document);
});
function initImageLoadRecovery() {
  const FALLBACK_IMAGE =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' fill='%23f2f2f2'/%3E%3C/svg%3E";
  const RETRY_PARAM = 'mp_img_retry';
  const FORMAT_FALLBACK_FLAG = 'mpImageFormatFallback';

  function buildRetryUrl(rawUrl) {
    if (!rawUrl || rawUrl.indexOf('data:') === 0) return null;
    try {
      const retryUrl = new URL(rawUrl, window.location.origin);
      if (retryUrl.searchParams.has(RETRY_PARAM)) return null;
      retryUrl.searchParams.set(RETRY_PARAM, Date.now().toString());
      return retryUrl.toString();
    } catch (error) {
      return null;
    }
  }

  function buildFormatFallbackUrl(rawUrl) {
    if (!rawUrl || rawUrl.indexOf('data:') === 0) return null;
    try {
      const fallbackUrl = new URL(rawUrl, window.location.origin);
      const formatParam = fallbackUrl.searchParams.get('format');
      if (!formatParam || formatParam.toLowerCase() !== 'webp') return null;
      fallbackUrl.searchParams.delete('format');
      return fallbackUrl.toString();
    } catch (error) {
      return null;
    }
  }

  function applyFallback(img) {
    if (!img || img.dataset.mpImageFallbackApplied === '1') return;
    img.dataset.mpImageFallbackApplied = '1';
    img.classList.add('mp-image-fallback');
    img.removeAttribute('srcset');
    img.src = FALLBACK_IMAGE;
  }

  function handleImageFailure(img) {
    if (!img || img.tagName !== 'IMG') return;
    if (!img.src || img.src.indexOf('data:') === 0) return;

    if (img.dataset[FORMAT_FALLBACK_FLAG] !== '1') {
      const formatFallbackUrl = buildFormatFallbackUrl(img.currentSrc || img.src);
      if (formatFallbackUrl) {
        img.dataset[FORMAT_FALLBACK_FLAG] = '1';
        img.src = formatFallbackUrl;
        return;
      }
    }

    const retryCount = Number(img.dataset.mpImageRetryCount || '0');
    if (retryCount < 1) {
      const retryUrl = buildRetryUrl(img.currentSrc || img.src);
      if (retryUrl) {
        img.dataset.mpImageRetryCount = String(retryCount + 1);
        img.removeAttribute('srcset');
        img.src = retryUrl;
        return;
      }
    }

    const fallbackSrc = img.dataset.fallbackSrc || img.getAttribute('data-fallback-src');
    if (fallbackSrc && img.src !== fallbackSrc) {
      img.dataset.mpImageRetryCount = String(retryCount + 1);
      img.removeAttribute('srcset');
      img.src = fallbackSrc;
      return;
    }

    applyFallback(img);
  }

  document.addEventListener(
    'error',
    (event) => {
      const target = event.target;
      if (target && target.tagName === 'IMG') handleImageFailure(target);
    },
    true
  );

  function recoverAlreadyFailedImages(root) {
    const scope = root || document;
    scope.querySelectorAll('img').forEach((img) => {
      if (img.complete && img.naturalWidth === 0) {
        handleImageFailure(img);
      }
    });
  }

  recoverAlreadyFailedImages(document);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!node || node.nodeType !== 1) return;
        if (node.tagName === 'IMG' && node.complete && node.naturalWidth === 0) {
          handleImageFailure(node);
          return;
        }
        recoverAlreadyFailedImages(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initImageLoadRecovery);
} else {
  initImageLoadRecovery();
}
